/**
 * JapanTollReceipts team — the same 5-member AI Operations team that
 * runs every NoJo service, here wearing JapanTollReceipts branded shirts.
 *
 * Used on the JapanTollReceipts service detail page
 * (`/:lang/services/japantollreceipts`) and mirrors the structure of
 * `jecTeam.js` so `ServiceTeamBand` can render both with one component.
 *
 * Portrait images live in /public/team/japantollreceipts/
 * Group / "in motion" photos live in /public/brand/jtr/
 *
 * Slugs match `src/data/aiTeam.js` so the same identities carry across
 * the corporate site, JEC, and JTR detail pages.
 */

export const JTR_TEAM = [
  {
    slug: "sora",
    portrait: "/team/japantollreceipts/member-3.webp",
    nameEn: "Sora",
    nameJa: "ソラ",
    titleEn: "Toll Reports Director",
    titleJa: "通行レポート・ディレクター",
    role: "director",
    bioEn:
      "Sora signs off on every report template, every customer-impacting MEISAI rule change, and every new pipeline before it goes out. Final approval lives with Sora.",
    bioJa:
      "レポートのテンプレート、お客様に影響するMEISAIルールの変更、新しい配信パイプラインについて、ソラは確認ポイントの整理を支援します。最終承認が必要な内容は、人によるレビューを経て対応します。",
  },
  {
    slug: "rin",
    portrait: "/team/japantollreceipts/member-1.webp",
    nameEn: "Rin",
    nameJa: "リン",
    titleEn: "JTR QA Lead",
    titleJa: "JTR QAリード",
    role: "qa",
    bioEn:
      "Rin cross-checks every PDF, every CSV, and every monthly recap before it lands in a customer's inbox. Anomalies route to a human — never auto-resolved.",
    bioJa:
      "PDF、CSV、月次レポートについて、リンは配信前の相互チェックを支援します。確認が必要なケースは必ず人によるレビューに回します。",
  },
  {
    slug: "yuki",
    portrait: "/team/japantollreceipts/member-2.webp",
    nameEn: "Yuki",
    nameJa: "ユキ",
    titleEn: "Senior AI Analyst — MEISAI Extraction",
    titleJa: "シニアAIアナリスト・MEISAI抽出担当",
    role: "analyst",
    bioEn:
      "Yuki runs the MEISAI extraction pipeline — pulling every trip, every toll, and every timestamp out of the official ETC usage record and matching it to the right plate.",
    bioJa:
      "MEISAIデータ確認パイプラインを担当。ETC利用照会サービスの利用記録から走行情報、料金、日時を整理し、該当する車両候補に紐づけます。",
  },
  {
    slug: "hana",
    portrait: "/team/japantollreceipts/member-5.webp",
    nameEn: "Hana",
    nameJa: "ハナ",
    titleEn: "Senior AI Analyst — Reports & Bilingual",
    titleJa: "シニアAIアナリスト・レポート/日英対応担当",
    role: "analyst",
    bioEn:
      "Hana assembles the daily, weekly, monthly, and year-end reports — and translates every customer-facing number into clean bilingual JA/EN deliverables.",
    bioJa:
      "日次・週次・月次・年末のレポート組み立てを担当。お客様向けの数値を、自然な日英のバイリンガル成果物に整えてお届けします。",
  },
  {
    slug: "aoi",
    portrait: "/team/japantollreceipts/member-4.webp",
    nameEn: "Aoi",
    nameJa: "アオイ",
    titleEn: "Senior AI Analyst — Support",
    titleJa: "シニアAIアナリスト・サポート担当",
    role: "analyst",
    bioEn:
      "Aoi answers JTR customer questions in English and Japanese — schedule changes, CSV format requests, audit-ready exports, anything the customer needs.",
    bioJa:
      "JTRご利用者様からの日英のお問い合わせを担当。配信スケジュールの変更、CSVフォーマットのリクエスト、確認用出力など、お客様が必要とする場面に対応します。",
  },
];

/**
 * Group photo gallery for the JTR detail page. Each photo carries a
 * bilingual location/caption so the story stays coherent (HQ city →
 * national reach → in the office building the AI workflow).
 */
export const JTR_TEAM_GROUP_PHOTOS = [
  {
    id: "jtr-office-dashboards",
    src: "/brand/jtr/jtr-team-office-dashboards.webp",
    locationEn: "Office · Live Dashboards",
    locationJa: "オフィス・ライブダッシュボード",
    captionEn: "The JTR team in the Yokosuka office, in front of the live dashboards.",
    captionJa: "横須賀のオフィスで、ライブダッシュボードを前にしたJTRチーム。",
  },
  {
    id: "jtr-ai-workflow",
    src: "/brand/jtr/jtr-team-ai-workflow.webp",
    locationEn: "Office · AI Workflow",
    locationJa: "オフィス・AIワークフロー",
    captionEn: "Reviewing the end-to-end AI workflow — extract, validate, classify, export.",
    captionJa: "抽出 → 確認 → 分類 → 出力。エンドツーエンドのAIワークフローをレビュー中。",
  },
  {
    id: "jtr-quality-assurance",
    src: "/brand/jtr/jtr-team-quality-assurance.webp",
    locationEn: "Office · Quality Assurance",
    locationJa: "オフィス・品質保証",
    captionEn: "QA review — process overview, performance trends, accuracy at 98.7%.",
    captionJa: "QAレビュー ─ プロセス全体、パフォーマンスの推移、精度98.7%を確認。",
  },
  {
    id: "jtr-workflow-display",
    src: "/brand/jtr/jtr-team-workflow-display.webp",
    locationEn: "Office · Pipeline Whiteboard",
    locationJa: "オフィス・パイプラインボード",
    captionEn: "Walking through the AI workflow at the holographic pipeline display.",
    captionJa: "ホログラフィックの工程表示の前で、AIワークフローをひとつずつ確認。",
  },
  {
    id: "jtr-laptop-dashboard",
    src: "/brand/jtr/jtr-team-laptop-dashboard.webp",
    locationEn: "Office · Customer Dashboard",
    locationJa: "オフィス・お客様ダッシュボード",
    captionEn: "Reviewing what the customer sees — the same dashboard they see from their inbox.",
    captionJa: "お客様が実際にご覧になるダッシュボードを、私たちも同じ画面で確認しています。",
  },
  {
    id: "jtr-ai-workflow-board",
    src: "/brand/jtr/jtr-team-ai-workflow-board.webp",
    locationEn: "Office · AI + OCR Pipeline",
    locationJa: "オフィス・AI+OCRパイプライン",
    captionEn: "Upload → Extract → Validate → Classify → Export. The receipt pipeline, end to end.",
    captionJa: "アップロード → 抽出 → 検証 → 分類 → 出力。ETC利用明細・利用証明書パイプラインの全体像。",
  },
  {
    id: "jtr-yokosuka-toll-gate",
    src: "/brand/jtr/jtr-team-yokosuka-toll-gate.webp",
    locationEn: "Yokosuka Toll Gate · HQ city",
    locationJa: "横須賀料金所・本社所在地",
    captionEn: "At the Yokosuka toll gate — the team in our HQ city.",
    captionJa: "横須賀料金所にて ─ 本社所在地のチーム。",
  },
  {
    id: "jtr-tokyo-toll-gate",
    src: "/brand/jtr/jtr-team-tokyo-toll-gate.webp",
    locationEn: "Tokyo Toll Gate",
    locationJa: "東京料金所",
    captionEn: "At the Tokyo toll gate — every trip we receipt starts at a gate like this.",
    captionJa: "東京料金所にて ─ 私たちが整理する一走行の記録は、こうした料金所から始まります。",
  },
];

export const JTR_TEAM_MANIFESTO = {
  eyebrowEn: "The five members behind every JTR report",
  eyebrowJa: "JTRレポート一通一通を支える5つのAI支援ロール",
  titleEn: "The five members in charge of every report we deliver.",
  titleJa: "お届けするレポート一通一通を支える、5つのAI支援ロール。",
  leadEn:
    "Every MEISAI extraction, every PDF receipt, every monthly recap — handled by the same five-member system, with a human final approval on anything that affects a customer.",
  leadJa:
    "MEISAIからの明細抽出、PDF形式の利用明細・利用証明書、月次レポート ─ そのすべてを、同じAI支援ワークフローが支えています。お客様に影響する判断はすべて、人による最終承認を経由します。",
};
